import React, { useRef, useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useLocation } from 'react-router-dom';
import logo from '../../logo.png';
import {
  SidebarWrapper, SidebarOverlay, LogoArea, LogoImage, LogoIcon,
  NavSectionWrapper, NavSection, ScrollButton,
  NavItem, NavIcon, NavLabel, Divider, ToggleButton
} from './Sidebar.styles';

// Items keyed `business-data` get rendered inside a single wrapper element
// so the tutorial spotlight can highlight Company + Offerings + General Asks
// + Objection Handling as a conceptual group ("Murphy's training inputs").
const BUSINESS_DATA_KEYS = new Set(['company', 'offerings', 'general-asks', 'objection-handling']);

const NAV_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: '\u{1F4CA}', path: '/dashboard' },
  { key: 'my-reports', label: 'My Reports', icon: '\u{1F6A6}', path: '/my-assessments-report' },
  { key: 'my-meetings', label: 'Live Meetings', icon: '\u{1F4DE}', path: '/live-meetings' },
  { key: 'team', label: 'Team', icon: '\u{1F465}', path: '/team-dashboard', hideForSalesMember: true },
  { key: 'reports', label: 'Reports', icon: '\u{1F4C8}', path: '/assessments-report', hideForSalesMember: true },
  { divider: true },
  { key: 'company', label: 'Company', icon: '\u{1F3E2}', path: '/company' },
  { key: 'offerings', label: 'Offerings', icon: '\u{1F4E6}', path: '/offerings' },
  { key: 'general-asks', label: 'General Asks', icon: '\u{1F4AC}', path: '/general-asks' },
  { key: 'objection-handling', label: 'Objection Handling', icon: '\u{2753}', path: '/objection-handling' },
  { divider: true },
  { key: 'train', label: 'Training Hub', icon: '\u{1F3AF}', path: '/training' },
  { divider: true },
  { key: 'playbook', label: 'Playbook', icon: '\u{1F4D6}', path: '/playbook' },
  { key: 'users', label: 'Users', icon: '\u{1F464}', path: '/users', managerOnly: true },
  { key: 'profile', label: 'Settings', icon: '\u{2699}\u{FE0F}', path: '/profile' },
  { key: 'admin', label: 'Admin', icon: '\u{1F6E0}\u{FE0F}', path: '/admin', adminOnly: true },
  { key: 'email', label: 'Email', icon: '\u{1F4E7}', path: '/email-admin', adminOnly: true },
  { key: 'murphy-meetings', label: 'Live Pipeline', icon: '\u{1F3A5}', path: '/murphy/meetings', adminOnly: true },
  { key: 'bot-inspector', label: 'Bot Inspector', icon: '\u{1F50D}', path: '/murphy/bot-inspector', adminOnly: true },
  { divider: true, adminOnly: true },
  { key: 'terms', label: 'Terms and Conditions', icon: '\u{1F4C4}', path: '/terms-conditions' },
];

const Sidebar = observer(({ collapsed, onToggle, mobileOpen, onMobileClose, currentUser, onLogout, history }) => {
  const routerHistory = useHistory();
  const location = useLocation();
  const nav = history || routerHistory;
  const navRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 4);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
  }, []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  const scrollBy = (amount) => {
    const el = navRef.current;
    if (el) el.scrollBy({ top: amount, behavior: 'smooth' });
  };

  const { isAdmin, isSalesMember, isReadOnlyExecutive, isReadOnlySalesManager, isReadOnlySeniorManager } = currentUser || {};
  const isReadOnly = isReadOnlyExecutive || isReadOnlySalesManager || isReadOnlySeniorManager;

  const isItemVisible = (item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.managerOnly && (isSalesMember || isReadOnly)) return false;
    if (item.hideForSalesMember && isSalesMember) return false;
    if (item.hideReadOnly && isReadOnly) return false;
    return true;
  };

  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleNavigate = (path) => {
    nav.push(path);
    if (onMobileClose) onMobileClose();
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      nav.push('/');
    }
  };

  return (
    <>
    <SidebarOverlay $open={mobileOpen} onClick={onMobileClose} />
    <SidebarWrapper $collapsed={collapsed} $mobileOpen={mobileOpen} data-tour="sidebar">
      <LogoArea $collapsed={collapsed}>
        {collapsed ? (
          <LogoIcon>M</LogoIcon>
        ) : (
          <LogoImage src={logo} alt="Multiply" />
        )}
      </LogoArea>

      <NavSectionWrapper>
        <ScrollButton $position="top" $visible={canScrollUp} onClick={() => scrollBy(-150)}>
          {'\u25B2'} More
        </ScrollButton>
        <NavSection ref={navRef}>
          {(() => {
            const out = [];
            let businessGroup = [];
            const flushBusinessGroup = () => {
              if (businessGroup.length === 0) return;
              out.push(
                <div key="business-data-group" data-tour="nav-business-data">
                  {businessGroup}
                </div>
              );
              businessGroup = [];
            };
            NAV_ITEMS.forEach((item, i) => {
              if (item.divider) {
                flushBusinessGroup();
                if (!isItemVisible(item)) return;
                out.push(<Divider key={`divider-${i}`} />);
                return;
              }
              if (!isItemVisible(item)) return;
              const node = (
                <NavItem
                  key={item.key}
                  data-tour={`nav-${item.key}`}
                  $active={isActive(item.path)}
                  $collapsed={collapsed}
                  onClick={() => handleNavigate(item.path)}
                  title={collapsed ? item.label : undefined}
                >
                  <NavIcon>{item.icon}</NavIcon>
                  <NavLabel $collapsed={collapsed}>{item.label}</NavLabel>
                </NavItem>
              );
              if (BUSINESS_DATA_KEYS.has(item.key)) {
                businessGroup.push(node);
              } else {
                flushBusinessGroup();
                out.push(node);
              }
            });
            flushBusinessGroup();
            return out;
          })()}

          <Divider />

          <NavItem
            $collapsed={collapsed}
            onClick={handleLogout}
            title={collapsed ? 'Sign Out' : undefined}
          >
            <NavIcon>{'\u{1F6AA}'}</NavIcon>
            <NavLabel $collapsed={collapsed}>Sign Out</NavLabel>
          </NavItem>
        </NavSection>
        <ScrollButton $position="bottom" $visible={canScrollDown} onClick={() => scrollBy(150)}>
          More {'\u25BC'}
        </ScrollButton>
      </NavSectionWrapper>

      <ToggleButton onClick={onToggle} title={collapsed ? 'Expand' : 'Collapse'}>
        {collapsed ? '\u{25B6}' : '\u{25C0}'}
      </ToggleButton>
    </SidebarWrapper>
    </>
  );
});

export default Sidebar;
